/**
 * Dropstack web app shell: the composition root.
 *
 * - Creates the app-wide services once (backend, audio, handwriting
 *   recogniser, purchases, ads) and wires them into the screens.
 * - Routes between screens with a tiny state machine (screens/model/router.ts).
 * - Applies settings to the page: language, accessibility classes on <html>,
 *   audio volumes, and the menu theme (CSS variables + animated background).
 * - Starts runs without ever blocking on the network, and submits finished
 *   runs in the background (screens/model/session.ts). A server ticket for
 *   newer game rules than this bundle's becomes an unranked offline run plus
 *   a "new version, reload" notice.
 * - Records the highest level each run reached (it unlocks that level's
 *   theme in the Profile gallery).
 * - Shows everything inside an Apple Watch frame for ?watch=1 or when the
 *   Watch preview setting is on.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { RunSummary } from '@dropstack/core';
import type { AudioEngine, BackendClient, HandwritingRecognizer, PlayerProfile, PlusFeatures, SessionTicket } from './contracts.ts';
import { setLocale } from './i18n/index.ts';
import { createAudioEngine } from './audio/createAudioEngine.ts';
import { createRecognizer } from './input/createRecognizer.ts';
import { createAdsService, type MockAdsService } from './services/ads.ts';
import { createBackend } from './services/backend/index.ts';
import { createPlusFeatures } from './services/plusFeatures.ts';
import { getPurchaseService, useEntitlements, type MockPurchaseService } from './services/purchases.ts';
import { randomSeed } from './services/random.ts';
import { getProgressStore, useProgress } from './state/progress.ts';
import { useSettings } from './state/settings.ts';
import { getAppStorage, STORAGE_KEYS } from './state/storage.ts';
import { BackgroundCanvas } from './theme/BackgroundCanvas.tsx';
import { applyThemeCssVars } from './theme/cssVars.ts';
import { RewardedAdModal } from './components/RewardedAdModal.tsx';
import { UpdateNotice } from './components/UpdateNotice.tsx';
import { ensureAppLinks } from './components/documentHead.ts';
import { usePageVisible } from './components/hooks.ts';
import { GameOverScreen } from './screens/GameOverScreen.tsx';
import { GameScreen } from './screens/GameScreen.tsx';
import { LeaderboardScreen } from './screens/LeaderboardScreen.tsx';
import { OnboardingScreen } from './screens/OnboardingScreen.tsx';
import { ProfileScreen } from './screens/ProfileScreen.tsx';
import { InGameSettings, SettingsScreen, type SettingsPanelProps } from './screens/SettingsScreen.tsx';
import { StartScreen } from './screens/StartScreen.tsx';
import { WatchFrame } from './screens/watch/WatchFrame.tsx';
import { WatchGameOver, WatchStart } from './screens/watch/WatchScreens.tsx';
import { htmlClassState, isWatchMode, menuLevel, themeForLevel, watchParam, withoutWatchParam } from './screens/model/appearance.ts';
import { needsRankRefresh, offlineResult, shouldSubmit, type FinishedRun, type SubmitState } from './screens/model/gameOver.ts';
import { profileInputFrom } from './screens/model/onboarding.ts';
import { INITIAL_ROUTER, canPlayFrom, currentRoute, routerReducer, type Route } from './screens/model/router.ts';
import { fetchRanks, startTicket } from './screens/model/session.ts';

// ───────────── App-wide services (one per page, StrictMode-safe) ─────────────

interface AppServices {
  /** Resolves once the startup health probe has picked the real API or the offline demo. */
  backend: Promise<BackendClient>;
  audio: AudioEngine;
  purchases: MockPurchaseService;
  plus: PlusFeatures;
  ads: MockAdsService;
  /** The single handwriting recogniser, created on first use (it builds its templates then). */
  recognizer(): HandwritingRecognizer;
}

let services: AppServices | null = null;

function appServices(): AppServices {
  if (!services) {
    const purchases = getPurchaseService();
    let recognizer: HandwritingRecognizer | null = null;
    services = {
      backend: createBackend(),
      audio: createAudioEngine(),
      purchases,
      plus: createPlusFeatures(purchases),
      ads: createAdsService(purchases),
      recognizer: () => (recognizer ??= createRecognizer()),
    };
  }
  return services;
}

type GameRoute = Extract<Route, { name: 'game' }>;

export function App() {
  const svc = appServices();
  const progressStore = getProgressStore();
  const [settings, updateSettings] = useSettings();
  const progress = useProgress(progressStore);
  const entitlements = useEntitlements(svc.purchases);
  const [router, dispatch] = useReducer(routerReducer, INITIAL_ROUTER);
  const [backend, setBackend] = useState<BackendClient | null>(null);
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [lastRun, setLastRun] = useState<FinishedRun | null>(null);
  const [inGameSettings, setInGameSettings] = useState(false);
  const [rewarded, setRewarded] = useState<{ resolve(outcome: 'completed' | 'skipped'): void } | null>(null);
  const [urlSearch, setUrlSearch] = useState(() => window.location.search);
  /** The server runs newer game rules than this bundle: 'shown' until the player closes the notice. */
  const [update, setUpdate] = useState<'none' | 'shown' | 'dismissed'>('none');
  /** Offline tickets that replaced a server ticket for newer rules (their result says "reload"). */
  const staleTickets = useRef(new Set<string>());
  const visible = usePageVisible();

  const route = currentRoute(router);
  const inGame = route.name === 'game';
  const watch = isWatchMode(urlSearch, settings.watchPreview);
  const adsEnabled = !entitlements.adsRemoved;

  // Once the player has left the start screen, coming back to it puts focus on
  // Play: the element that had focus on the previous screen is gone.
  const [leftStart, setLeftStart] = useState(false);
  useEffect(() => {
    if (route.name !== 'start') setLeftStart(true);
  }, [route.name]);

  useEffect(() => ensureAppLinks(), []);

  // ───────────── Settings → page ─────────────

  useLayoutEffect(() => setLocale(settings.locale), [settings.locale]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    for (const [className, on] of htmlClassState(settings)) root.classList.toggle(className, on);
    root.classList.toggle('watch-mode', watch);
  }, [settings, watch]);

  useEffect(() => svc.audio.setVolumes(settings.audio), [svc, settings.audio]);

  // Audio may only start after a user gesture; unlock on the first one anywhere.
  useEffect(() => {
    const unlock = () => {
      void svc.audio.unlock();
      remove();
    };
    const remove = () => {
      window.removeEventListener('click', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
    window.addEventListener('click', unlock, true);
    window.addEventListener('keydown', unlock, true);
    return remove;
  }, [svc]);

  // ───────────── Menu theme ─────────────

  // On the game-over screen, dress the menus in the level the run just reached.
  const menuRating =
    route.name === 'gameOver' && lastRun && shouldSubmit(lastRun.summary) ? lastRun.summary.savedRating : progress.savedRating;
  const level = menuLevel(menuRating);
  const theme = useMemo(() => themeForLevel(level), [level]);

  // During a run the game screen owns the theme; menus get theirs back afterwards.
  useLayoutEffect(() => {
    if (!inGame) applyThemeCssVars(theme);
  }, [theme, inGame]);

  useEffect(() => {
    if (!inGame) svc.audio.setTheme(theme);
  }, [svc, theme, inGame]);

  // ───────────── Backend & profile ─────────────

  useEffect(() => {
    let live = true;
    void svc.backend.then((b) => {
      if (!live) return;
      setBackend(b);
      // First launch: register the anonymous player silently in the background.
      b.ensurePlayer().then(
        (p) => live && setProfile(p),
        () => undefined, // offline: tried again when a run starts
      );
    });
    return () => {
      live = false;
    };
  }, [svc]);

  // The profile's opt-in is authoritative; the setting mirrors it.
  useEffect(() => {
    if (profile?.onboarded && profile.leaderboardOptIn !== settings.leaderboardOptIn) {
      updateSettings({ leaderboardOptIn: profile.leaderboardOptIn });
    }
  }, [profile, settings.leaderboardOptIn, updateSettings]);

  // ───────────── Rewarded ads ─────────────

  useEffect(
    () => svc.ads.setRewardedPresenter(() => new Promise((resolve) => setRewarded({ resolve }))),
    [svc],
  );

  // ───────────── Runs ─────────────

  const play = () => {
    if (router.starting || !canPlayFrom(route)) return;
    void svc.audio.unlock(); // inside the click: satisfies the autoplay policy
    dispatch({ type: 'play' });
    const onRulesMismatch = ({ ticket }: { ticket: SessionTicket }) => {
      staleTickets.current.add(ticket.sessionId);
      setUpdate((u) => (u === 'none' ? 'shown' : u));
    };
    const deps = {
      backend: svc.backend,
      progress: progressStore.get,
      seed: randomSeed,
      now: Date.now,
      relaxed: settings.relaxedPace,
      onRulesMismatch,
    };
    void startTicket(deps).then((ticket) => {
      dispatch({ type: 'playReady', ticket });
      // The player may have been registered just now (offline at launch): pick up the profile.
      if (!profile) void svc.backend.then((b) => b.ensurePlayer()).then(setProfile, () => undefined);
    });
  };

  const sendRun = useCallback(
    async (run: FinishedRun) => {
      const set = (submit: SubmitState) => setLastRun((cur) => (cur && cur.run === run.run ? { ...cur, submit } : cur));
      set({ status: 'pending' });
      if (run.ticket.offline) {
        // Nothing to send: keep the rating on this device so the next run warm-starts.
        progressStore.recordRating(run.summary.savedRating);
        const reason = staleTickets.current.has(run.ticket.sessionId) ? 'rules-version' : 'offline';
        set({ status: 'done', result: offlineResult(run.summary, reason, run.ticket.relaxed) });
        return;
      }
      try {
        const b = await svc.backend;
        set({ status: 'done', result: await b.submitRun(run.ticket, run.summary) });
      } catch {
        set({ status: 'failed' });
      }
    },
    [svc, progressStore],
  );

  const finishedRun = useRef(0);
  const finishRun = (game: GameRoute, summary: RunSummary, info: { quit: boolean }) => {
    if (finishedRun.current === game.run) return; // one result per run
    finishedRun.current = game.run;
    const run: FinishedRun = {
      run: game.run,
      summary,
      ticket: game.ticket,
      quit: info.quit,
      submit: shouldSubmit(summary) ? { status: 'pending' } : { status: 'skipped' },
    };
    progressStore.recordLevelReached(summary.levelReached);
    setInGameSettings(false);
    setLastRun(run);
    dispatch({ type: 'gameOver' });
    if (run.submit.status === 'pending') void sendRun(run);
  };

  // After joining the leaderboard from the game-over screen, the stored run now
  // counts (unless it was the placement run): look up its ranks once.
  const ranksLoadedFor = useRef(0);
  useEffect(() => {
    if (!backend || !lastRun || !needsRankRefresh(lastRun, profile)) return;
    if (ranksLoadedFor.current === lastRun.run) return;
    const runId = lastRun.run;
    ranksLoadedFor.current = runId;
    void fetchRanks(backend).then((ranks) =>
      setLastRun((cur) =>
        cur && cur.run === runId && cur.submit.status === 'done'
          ? { ...cur, submit: { ...cur.submit, result: { ...cur.submit.result, ranks } } }
          : cur,
      ),
    );
  }, [backend, lastRun, profile]);

  // ───────────── Profile & settings actions ─────────────

  const openJoin = () =>
    dispatch({
      type: 'open',
      route: { name: 'onboarding', mode: 'join', step: profile?.onboarded ? 'leaderboard' : 'name' },
    });

  const onProfileSaved = (saved: PlayerProfile) => {
    setProfile(saved);
    updateSettings({ leaderboardOptIn: saved.leaderboardOptIn });
    dispatch({ type: 'back' });
  };

  const saveOptIn = async (optIn: boolean): Promise<boolean> => {
    const input = profile && profileInputFrom(profile, { leaderboardOptIn: optIn, locale: settings.locale });
    if (!input) return false;
    try {
      const saved = await (await svc.backend).saveProfile(input);
      setProfile(saved);
      updateSettings({ leaderboardOptIn: saved.leaderboardOptIn });
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Start over as a new player on this device: forget the player (credentials,
   * cached profile, offline-demo data) and the local rating. Stickers,
   * purchases, settings and handwriting samples stay.
   */
  const resetProgress = async () => {
    const store = getAppStorage();
    for (const key of [STORAGE_KEYS.auth, STORAGE_KEYS.profileCache, STORAGE_KEYS.offlineDb]) store.remove(key);
    progressStore.reset();
    setLastRun(null);
    setProfile(null);
    try {
      setProfile(await (await svc.backend).ensurePlayer());
    } catch {
      // Offline: the new player is registered when the next run starts.
    }
  };

  const clearWatchParam = () => {
    window.history.replaceState(window.history.state, '', withoutWatchParam(window.location.href));
    setUrlSearch(window.location.search);
  };

  const setWatchPreview = (on: boolean) => {
    if (!on && watchParam(urlSearch) !== null) clearWatchParam();
    updateSettings({ watchPreview: on });
    if (on) dispatch({ type: 'home' });
  };

  const exitWatch = () => setWatchPreview(false);

  /** Built on demand: it creates the handwriting recogniser, which only Settings and the game need. */
  const settingsProps = (): Omit<SettingsPanelProps, 'context'> => ({
    settings,
    onChange: updateSettings,
    recognizer: svc.recognizer(),
    profile,
    onSaveOptIn: saveOptIn,
    onJoin: openJoin,
    purchases: svc.purchases,
    entitlements,
    plusFeatures: svc.plus,
    watchActive: watch,
    onWatchPreview: setWatchPreview,
    onResetProgress: resetProgress,
  });

  // ───────────── Screens ─────────────

  const renderGame = (game: GameRoute) => (
    <GameScreen
      key={game.run}
      settings={settings}
      ticket={game.ticket}
      audio={svc.audio}
      recognizer={svc.recognizer()}
      ads={svc.ads}
      compact={watch}
      onGameOver={(summary, info) => finishRun(game, summary, info)}
      onOpenSettings={watch ? undefined : () => setInGameSettings(true)}
    />
  );

  const renderStart = () => (
    <StartScreen
      level={level}
      theme={theme}
      locale={settings.locale}
      starting={router.starting}
      offlineDemo={backend?.kind === 'offline-demo'}
      ads={svc.ads}
      adsEnabled={adsEnabled}
      focusPlay={leftStart}
      onPlay={play}
      onOpen={(name) => dispatch({ type: 'open', route: { name } })}
    />
  );

  const renderScreen = () => {
    switch (route.name) {
      case 'start':
        return renderStart();
      case 'game':
        return renderGame(route);
      case 'gameOver':
        return lastRun ? (
          <GameOverScreen
            run={lastRun}
            profile={profile}
            joinDismissed={progress.onboardingPromptDismissed}
            locale={settings.locale}
            starting={router.starting}
            ads={svc.ads}
            adsEnabled={adsEnabled}
            onPlayAgain={play}
            onLeaderboards={() => dispatch({ type: 'open', route: { name: 'leaderboards' } })}
            onHome={() => dispatch({ type: 'home' })}
            onJoin={openJoin}
            onDismissJoin={() => progressStore.setOnboardingPromptDismissed(true)}
            onRetrySubmit={() => void sendRun(lastRun)}
          />
        ) : (
          renderStart()
        );
      case 'settings':
        return <SettingsScreen {...settingsProps()} onBack={() => dispatch({ type: 'back' })} />;
      case 'leaderboards':
        return (
          <LeaderboardScreen
            backend={backend}
            profile={profile}
            locale={settings.locale}
            onBack={() => dispatch({ type: 'back' })}
            onJoin={openJoin}
          />
        );
      case 'profile':
        return (
          <ProfileScreen
            profile={profile}
            stickers={progress.stickers}
            highestLevelReached={progress.highestLevelReached}
            locale={settings.locale}
            adsEnabled={adsEnabled}
            rewardedLeftToday={svc.ads.rewardedLeftToday()}
            onEdit={(step) => dispatch({ type: 'open', route: { name: 'onboarding', mode: 'edit', step } })}
            onJoin={openJoin}
            onEarnSticker={() => svc.ads.showRewarded('theme-sticker')}
            onStickerEarned={(id) => progressStore.addSticker(id)}
            onBack={() => dispatch({ type: 'back' })}
          />
        );
      case 'onboarding':
        return (
          <OnboardingScreen
            key={`${route.mode}-${route.step}`}
            mode={route.mode}
            step={route.step}
            backend={backend}
            profile={profile}
            locale={settings.locale}
            onSaved={onProfileSaved}
            onSkip={() => dispatch({ type: 'back' })}
          />
        );
    }
  };

  const renderWatch = () => (
    <WatchFrame onExit={exitWatch} canExit={!inGame}>
      {route.name === 'game' ? (
        renderGame(route)
      ) : route.name === 'gameOver' && lastRun ? (
        <WatchGameOver run={lastRun} starting={router.starting} onPlayAgain={play} onHome={() => dispatch({ type: 'home' })} />
      ) : (
        <WatchStart level={level} starting={router.starting} onPlay={play} />
      )}
    </WatchFrame>
  );

  return (
    <div className={`app app--${route.name}${watch ? ' app--watch' : ''}`}>
      {(!inGame || watch) && (
        <div className="app__backdrop" aria-hidden="true">
          <BackgroundCanvas theme={theme} reducedMotion={settings.reducedMotion} paused={!visible || inGame} />
        </div>
      )}
      <div className="app__content">{watch ? renderWatch() : renderScreen()}</div>
      {inGameSettings && inGame && !watch && (
        <InGameSettings {...settingsProps()} onClose={() => setInGameSettings(false)} />
      )}
      {update === 'shown' && !inGame && (
        <UpdateNotice onReload={() => window.location.reload()} onDismiss={() => setUpdate('dismissed')} />
      )}
      {rewarded && (
        <RewardedAdModal
          onFinish={(outcome) => {
            rewarded.resolve(outcome);
            setRewarded(null);
          }}
        />
      )}
    </div>
  );
}
